import { useEffect } from 'react';
import './App.scss';
import { getColor, setThemeColor } from './theme/colors/colors';
import { ThemeProvider } from '@mui/material/styles';
import { muiTheme } from './theme/mui-theme/mui-theme';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { Callback } from './components/Callback';
import { AuthenticationGuard } from './components/auth/AuthGuard';
import styled from '@emotion/styled';
import List from './components/deals/list/List';
import Create from './components/deals/create/Create';
import { getVar } from './theme/ui-variables/ui-variables';
import Header from './components/shared/header/Header';
import Sidebar from './components/shared/sidebar/Sidebar';
import { useAuth0 } from '@auth0/auth0-react';
import { registerAxiosInterceptors } from './api/api';
import BuyerSeatForm from './components/buyer-seat/BuyerSeatForm';
import BuyerSeatDetails from './components/buyer-seat/BuyerSeatDetails';
import BuyerSeatList from './components/buyer-seat/BuyerSeatList';
import CreateSegment from './components/deals/create/create-segment/CreateSegment';
import AlertComponent from './components/shared/AlertComponent';
import { AlertProvider } from './components/shared/AlertProvider';
import Organization from './components/organization/create/Organization';
import OrganizationDetails from './components/organization/detail/OrganizationDetails';
import OrganizationList from './components/organization/list/OrganizationList';
import ScrollToTop from './components/shared/ScrollToTop';
import Home from './components/home/Home';
import TermsConditions from './components/terms-conditions/TermsConditions';
import { AuthenticationTermGuard } from './components/auth/AuthTermGuard';
import { WarrantGuard } from './components/auth/WarrantGuard';
import { AdminGuard } from './components/auth/AdminGuard';
import SideBarWithMainPage from './components/home/SideBarWithMainPage';

function App() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
  if (!audience) {
    throw new Error(`Auth0 Audience is not defined: ${audience}`);
  }

  useEffect(() => {
    let refreshToken = () =>
      getAccessTokenSilently({
        cacheMode: 'on',
        authorizationParams: {
          scope: 'openid profile email offline_access',
          audience: audience,
        },
      }).catch((err) => {
        console.info(`Refresh token expired: ${err}`);
        console.info('Redirecting to login.');
        loginWithRedirect({ appState: { returnTo: window.location.pathname } }).then();
      });

    registerAxiosInterceptors(refreshToken);
  }, []);

  useEffect(() => {
    setThemeColor();
  }, []);

  return (
    <ThemeProvider theme={muiTheme}>
      <PageContainer>
        <Header />
        <Routes>
          <Route path="/" element={<AuthenticationTermGuard component={Home} />} >
            <Route path="/" element={<WarrantGuard component={SideBarWithMainPage} />} >
              <Route index element={<Navigate to="deal" replace />} />
              <Route path="deal" element={<List/> }/>
              <Route path="deal/new" element={< Create/>} />
              <Route path="deal/edit/:id" element={<Create/>} />
              <Route path="buyer-seat/list" element={<AdminGuard component={BuyerSeatList} />} />
              <Route path="buyer-seat/list/:id" element={<AdminGuard component={BuyerSeatDetails} />} />
              <Route path="buyer-seat" element={<AdminGuard component={BuyerSeatForm} />} />
              <Route path="organization" element={<AdminGuard component={Organization} />} />
              <Route path="organization/list" element={<AdminGuard component={OrganizationList} />} />
              <Route path="organization/list/:id" element={<AdminGuard component={OrganizationDetails} />} />
              <Route path="*" element={<NoMatch />} />
            </Route>
          </Route>
          <Route path="/terms-and-conditions" element={<AuthenticationTermGuard component={TermsConditions} />} />
          <Route path="/callback" element={<Callback />} />

        </Routes>
      </PageContainer>
    </ThemeProvider>
  );
}

export default App;

// Todo: Implement a proper 404 component
const NoMatch = () => {
  return (
    <div>
      <h2>404 Not Found</h2>
      <p>
        <Link to="/">Go to the home page</Link>
      </p>
    </div>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const SidebarAndMainWrapper = styled.div`
  display: flex;
  height: calc(100% - ${getVar('headerHeight')});;
  overflow: hidden;
`;

const Main = styled.main`
  width: calc(100% - ${getVar('sidebarWidth')});
  height: 100%;
  padding: ${getVar('mainPadding')};
  background-color: ${getColor('secondary')};
  overflow-y: auto;
`;
